import { Avatar } from '@components/avatar';
import { ForemanProps } from './types';
import { cn } from '@utils/cn';
import { useTranslation } from 'react-i18next';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { AppTooltip } from '../tooltip/tooltip';
import { ReactNode, useEffect, useState } from 'react';
import { CrewAvatar } from '@components/crewAvatar';
import { EquipmentAvatar } from '@components/equipmentAvatar';
import { useSchedulerContext } from '@contexts/scheduler';

export const Foreman = ({ data, type, conflicts }: ForemanProps) => {
  const { t } = useTranslation('app');
  const { modal, active } = useSchedulerContext();
  const [showConflicts, setShowConflicts] = useState<boolean>(false);

  useEffect(() => {
    if (!data) return;
    if (!modal) return;
    if (!active?.schedule) return;
    if (!conflicts?.length) return;

    const remainingConflicts = conflicts.filter(
      (conflict) => conflict.scheduleId !== active?.schedule?.id && conflict.foremanId === data?.id,
    );
    setShowConflicts(remainingConflicts.length > 0);
  }, [modal, active, conflicts, data]);

  if (!data) return null;

  const { fullName, specialties, discardedAt, avatarType } = data;
  const mergedSpecialties = specialties
    ? specialties.map(({ name }) => name).join(', ')
    : undefined;

  const getColor = () => {
    if (discardedAt) return 'bg-neutral-300';
    if (fullName && !['crew_member', 'equipment_member'].includes(avatarType || ''))
      return 'bg-primary-300';
    if (avatarType === 'crew_member') return 'bg-blue-400';
    if (avatarType === 'equipment_member') return 'bg-purple-400';
    return 'bg-neutral-200';
  };

  const getName = () => {
    if (discardedAt) return;
    if (fullName) return fullName;
    return '?';
  };

  const withTooltip = (component: ReactNode) => {
    if (discardedAt) {
      return (
        <AppTooltip side="bottom" align="start" context={<span>{t('pages.users.removed')}</span>}>
          {component}
        </AppTooltip>
      );
    }
    if (mergedSpecialties) {
      return (
        <AppTooltip side="bottom" align="start" context={<span>{mergedSpecialties}</span>}>
          {component}
        </AppTooltip>
      );
    }
    return component;
  };

  const crewAvatar = () => {
    return (
      <CrewAvatar
        defaultChildren={!discardedAt && <SvgConsumer id="crew" />}
        hideTooltip
        className={cn(['h-6 w-6 select-none text-xs', getColor(), discardedAt && 'cursor-help'])}>
        {discardedAt && <SvgConsumer className="shrink-0 text-sm" id="inactiveCrew" />}
      </CrewAvatar>
    );
  };

  const equipmentAvatar = () => {
    return (
      <EquipmentAvatar
        defaultChildren={!discardedAt && <SvgConsumer id="equipment" />}
        hideTooltip
        className={cn(['h-6 w-6 select-none text-xs', getColor(), discardedAt && 'cursor-help'])}>
        {discardedAt && <SvgConsumer className="shrink-0 text-sm" id="inactiveEquipment" />}
      </EquipmentAvatar>
    );
  };

  const getAvatarType = () => {
    if (avatarType === 'crew_member') return crewAvatar();
    if (avatarType === 'equipment_member') return equipmentAvatar();

    return (
      <Avatar
        name={getName()}
        hideTooltip
        className={cn(['h-6 w-6 select-none text-xs', getColor(), discardedAt && 'cursor-help'])}>
        {discardedAt && <SvgConsumer className="ml-[2px] shrink-0 text-sm" id="removePerson" />}
      </Avatar>
    );
  };

  return (
    <div className="flex relative flex-col w-full h-full gap-1">
      <div className="flex h-full w-full items-center gap-2">
        {withTooltip(
          <div className={cn(['h-6 w-6', (discardedAt || mergedSpecialties) && 'cursor-help'])}>
            {getAvatarType()}
          </div>,
        )}
        <div className="flex flex-col truncate">
          <span
            className={cn([
              'truncate text-sm font-semibold',
              fullName && !discardedAt ? 'text-neutral-600' : 'text-neutral-400',
            ])}>
            {fullName || t('pages.foreman.scheduledWithoutTeam')}
          </span>
          {(type || mergedSpecialties) && (
            <span
              className="truncate text-[10px] leading-3 text-neutral-400 cursor-help"
              title={mergedSpecialties ? mergedSpecialties : undefined}>
              {mergedSpecialties || type}
            </span>
          )}
        </div>
      </div>
      {showConflicts ? (
        <div className="flex absolute left-1 h-fit bottom-[-10px] mt-0 flex-row gap-1">
          <SvgConsumer id="warning" className="shrink-0 text-sm text-warning-600" />
          <span className="ml-[2px] text-warning-600 font-normal text-xs">
            {t('schedule.conflicts')}
          </span>
        </div>
      ) : null}
    </div>
  );
};
